import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaEye,
  FaLink,
  FaSyncAlt,
  FaEnvelope,
} from "react-icons/fa";
import ThemeToggle from "../../components/ThemeToggle";

export default function PrivacyPolicy({ theme, setTheme }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300 relative">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200 shadow-sm border border-gray-200 dark:border-gray-700 z-10"
      >
        <FaArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back</span>
      </button>

      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle theme={theme} setTheme={setTheme} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-12 pt-16">
          <div className="bg-indigo-900 dark:bg-indigo-950 rounded-lg p-8 md:p-12 shadow-lg border border-indigo-800 dark:border-indigo-900">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-indigo-800 dark:bg-indigo-900 rounded-lg">
                <FaShieldAlt className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Privacy Policy
              </h1>
            </div>
            <p className="text-indigo-100 text-lg leading-relaxed max-w-3xl">
              At Spa City Partners LLC DBA Newlogiq, we are committed to
              protecting the privacy and security of our clients and website
              visitors. This Privacy Policy outlines how we collect, use,
              disclose, and safeguard your information when you visit our
              website or engage our consulting services.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaUserShield className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Information We Collect
              </h2>
            </div>

            <div className="mb-4 pl-2">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">
                1.1 Personal Information:
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                When you contact us through our website, we may collect personal
                information such as your name, email address, phone number, and
                company name to respond to your inquiries and provide the
                requested services.
              </p>
            </div>

            <div className="mb-4 pl-2">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">
                1.2 Website Usage Information:
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may collect non-personal information about your interaction
                with our website, including your IP address, browser type,
                operating system, referring URLs, and actions taken on our site,
                through the use of cookies and other tracking technologies.
              </p>
            </div>
          </section>

          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaEye className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                How We Use Your Information
              </h2>
            </div>

            <div className="mb-4 pl-2">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">
                2.1 To Provide Services:
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We use your personal information to respond to your inquiries,
                provide the requested consulting services, and communicate with
                you regarding your engagement with Newlogiq Consulting.
              </p>
            </div>

            <div className="mb-4 pl-2">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">
                2.2 To Improve Our Website:
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may use website usage information to analyze trends,
                administer the site, track users' movements, and gather broad
                demographic information for aggregate use to improve our website
                and services.
              </p>
            </div>
          </section>

          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaShieldAlt className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Information Sharing and Disclosure
              </h2>
            </div>

            <div className="mb-4 pl-2">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">
                3.1 Third-Party Service Providers:
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may engage trusted third-party service providers to assist us
                in operating our website and conducting our business. These
                providers are obligated to maintain the confidentiality of your
                information and are prohibited from using it for any purpose
                other than to provide the requested services.
              </p>
            </div>

            <div className="mb-4 pl-2">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">
                3.2 Legal Requirements:
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may disclose your personal information if required to do so
                by law or in response to valid requests by public authorities,
                such as to meet national security or law enforcement
                requirements.
              </p>
            </div>
          </section>

          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaLock className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Data Security
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We implement reasonable security measures to protect your personal
              information from unauthorized access, alteration, disclosure, or
              destruction. However, no method of transmission over the internet
              or electronic storage is 100% secure, and we cannot guarantee
              absolute security.
            </p>
          </section>

          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaLink className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                External Links
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Our website may contain links to external sites that are not
              operated by us. We have no control over the content and practices
              of these sites and encourage you to review their respective
              privacy policies.
            </p>
          </section>

          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaSyncAlt className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Changes to This Privacy Policy
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We reserve the right to update or change our Privacy Policy at any
              time. Any changes will be posted on this page with an updated
              effective date.
            </p>
          </section>

          <section className="mb-8 p-6 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-shadow duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaEnvelope className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Contact Us
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If you have any questions or concerns about our Privacy Policy or
              our data practices, please contact us at{" "}
              <a
                href="mailto:info@newlogiq.com"
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold underline decoration-2 underline-offset-2 transition-colors"
              >
                info@newlogiq.com
              </a>
              .
            </p>
          </section>

          <div className="mt-8 p-6 bg-indigo-900 dark:bg-indigo-950 rounded-lg shadow-lg border border-indigo-800 dark:border-indigo-900">
            <p className="text-white leading-relaxed font-medium text-center">
              By using our website and engaging our services, you signify your
              acceptance of this Privacy Policy. If you do not agree with this
              policy, please refrain from using our website or providing
              personal information to us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
